// ante-react-kit/src/components/v2/AntAddressPicker/index.js
import React, { useState, useCallback, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import './index.scss';
import AntLocalization from '../../../utils/AntLocalization';
import AntPopup from '../../AntPopup';
import AntAddress from '../../../utils/AntAddress';
import AntLocalStorage from '../../../utils/AntLocalStorage';
import AntAddressSearch from '../AntAddressSearch';
import { useAddressSearchEnabled } from '../AntAddressSearch/capability';

var EMPTY_ADDR = {
    contact_name: '', contact_phone: '', address: '',
    country_code: '', postal_code: '',
    adm1_code: '', adm1_name: '', adm2_code: '', adm2_name: '', adm3_code: '', adm3_name: ''
};

var AntAddressPicker = forwardRef(function AntAddressPicker(props, ref) {
    var { API, session, onSelect, savedAddresses } = props;
    var popupRef = useRef(null);
    // Mỗi business hoạt động trong 1 nước → country cố định theo Sys_BaseCountry (không cho đổi nước).
    var country = AntLocalStorage.getBusinessSetting('Sys_BaseCountry') || 'VN';
    var locale = AntLocalStorage.getBusinessSetting('Sys_BaseLanguage') || 'vi';
    var addr = new AntAddress(country, locale); // rẻ, chỉ đọc cache module-level
    var searchEnabled = useAddressSearchEnabled(country); // chưa khai khoá thì ẩn ô tìm nhanh
    var [activeTab, setActiveTab] = useState('saved');
    var [newAddr, setNewAddr] = useState(Object.assign({}, EMPTY_ADDR, { country_code: country }));
    var [provinces, setProvinces] = useState([]);
    var [districts, setDistricts] = useState([]);
    var [wards, setWards] = useState([]);
    var [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, function () {
        return {
            show: function () {
                setActiveTab((savedAddresses && savedAddresses.length > 0) ? 'saved' : 'new');
                setNewAddr(Object.assign({}, EMPTY_ADDR, { country_code: country }));
                setDistricts([]);
                setWards([]);
                setIsOpen(true);
                if (popupRef.current) popupRef.current.show();
            },
            hide: function () {
                setIsOpen(false);
                if (popupRef.current) popupRef.current.close();
            }
        };
    });

    // Nạp envelope nước của shop → load cấp 1 (envelope nạp runtime qua AntAddress.ensure).
    useEffect(function () {
        if (!isOpen) return;
        AntAddress.ensure(country, API).then(function () {
            setProvinces(new AntAddress(country, locale).getProvinces());
        });
    }, [API, isOpen, country, locale]);

    var onProvinceChange = useCallback(function (code) {
        var a = new AntAddress(country, locale);
        setNewAddr(function (v) {
            return Object.assign({}, v, {
                adm1_code: code, adm1_name: a.getUnitName(1, code) || '',
                adm2_code: '', adm2_name: '', adm3_code: '', adm3_name: ''
            });
        });
        setWards([]);
        setDistricts(code ? a.getDistricts(code) : []);
    }, [country, locale]);

    var onDistrictChange = useCallback(function (code) {
        var a = new AntAddress(country, locale);
        setNewAddr(function (v) {
            return Object.assign({}, v, { adm2_code: code, adm2_name: a.getUnitName(2, code) || '', adm3_code: '', adm3_name: '' });
        });
        setWards(code ? a.getWards(code) : []);
    }, [country, locale]);

    var onWardChange = useCallback(function (code) {
        var a = new AntAddress(country, locale);
        setNewAddr(function (v) {
            return Object.assign({}, v, { adm3_code: code, adm3_name: a.getUnitName(3, code) || '' });
        });
    }, [country, locale]);

    // Chọn từ ô tìm Google → điền địa chỉ + cascade hành chính (nạp sẵn quận/phường theo mã đã map).
    var onSearchSelect = useCallback(function (d) {
        var a = new AntAddress(country, locale);
        setNewAddr(function (v) {
            return Object.assign({}, v, {
                address: d.entity_address || v.address,
                postal_code: d.postal_code || '',
                adm1_code: d.adm1_code || '', adm1_name: d.adm1_name || '',
                adm2_code: d.adm2_code || '', adm2_name: d.adm2_name || '',
                adm3_code: d.adm3_code || '', adm3_name: d.adm3_name || ''
            });
        });
        setDistricts(d.adm1_code ? a.getDistricts(d.adm1_code) : []);
        setWards(d.adm2_code ? a.getWards(d.adm2_code) : []);
    }, [country, locale]);

    var closePopup = useCallback(function () {
        setIsOpen(false);
        if (popupRef.current) popupRef.current.close();
    }, []);

    var selectSaved = useCallback(function (addr) {
        if (onSelect) onSelect(addr);
        closePopup();
    }, [onSelect, closePopup]);

    var submitNew = useCallback(function () {
        if (!newAddr.address && !newAddr.contact_name) return;
        if (onSelect) onSelect(Object.assign({}, newAddr, { isNew: true }));
        closePopup();
    }, [newAddr, onSelect, closePopup]);

    var updateField = useCallback(function (field, value) {
        setNewAddr(function (v) {
            var obj = {};
            obj[field] = value;
            return Object.assign({}, v, obj);
        });
    }, []);

    var title = AntLocalization.translateText({ vi: 'Địa chỉ giao hàng', en: 'Delivery Address', fr: 'Adresse de livraison', es: 'Dirección de entrega', id: 'Alamat pengiriman', ja: '配送先住所', zh: '收货地址', ko: '배송 주소', hi: 'डिलीवरी पता', ar: 'عنوان التسليم' });

    // Nhãn cấp lấy từ envelope theo nước của shop (đa ngôn ngữ); fallback generic khi chưa nạp.
    var levelCount = addr.getLevelCount();
    var mode = addr.getMode();
    var lbl1 = addr.getLevelLabelText(1) || AntLocalization.translateText({ vi: 'Tỉnh/Thành', en: 'Province', fr: 'Province', es: 'Provincia', id: 'Provinsi', ja: '省/市', zh: '省/市', ko: '도/시', hi: 'प्रांत', ar: 'المحافظة' });
    var lbl2 = addr.getLevelLabelText(2) || AntLocalization.translateText({ vi: 'Quận/Huyện', en: 'District', fr: 'District', es: 'Distrito', id: 'Distrik', ja: '区/郡', zh: '区/县', ko: '구/군', hi: 'ज़िला', ar: 'المنطقة' });
    var lbl3 = addr.getLevelLabelText(3) || AntLocalization.translateText({ vi: 'Phường/Xã', en: 'Ward', fr: 'Quartier', es: 'Barrio', id: 'Kelurahan', ja: '町/村', zh: '街道/乡', ko: '동/면', hi: 'वार्ड', ar: 'الحي' });

    return (
        <AntPopup ref={popupRef} title={title} width={560} height={480} onClosed={function () { setIsOpen(false); }}>
            <div className="v2-address-picker">
                {/* Tab bar */}
                <div className="v2-address-picker__tabs">
                    <button
                        className={'v2-address-picker__tab' + (activeTab === 'saved' ? ' v2-address-picker__tab--active' : '')}
                        onClick={function () { setActiveTab('saved'); }}
                    >
                        {AntLocalization.translateText({ vi: 'Địa chỉ đã lưu', en: 'Saved Addresses', fr: 'Enregistrées', es: 'Guardadas', id: 'Alamat tersimpan', ja: '保存済み住所', zh: '已保存地址', ko: '저장된 주소', hi: 'सहेजे गए पते', ar: 'العناوين المحفوظة' })}
                    </button>
                    <button
                        className={'v2-address-picker__tab' + (activeTab === 'new' ? ' v2-address-picker__tab--active' : '')}
                        onClick={function () { setActiveTab('new'); }}
                    >
                        {AntLocalization.translateText({ vi: 'Nhập mới', en: 'New Address', fr: 'Nouvelle', es: 'Nueva', id: 'Alamat baru', ja: '新規入力', zh: '新地址', ko: '새 주소', hi: 'नया पता', ar: 'عنوان جديد' })}
                    </button>
                </div>

                {/* Saved tab */}
                {activeTab === 'saved' && (
                    <div className="v2-address-picker__saved">
                        {(!savedAddresses || savedAddresses.length === 0) && (
                            <div className="v2-address-picker__empty">
                                {AntLocalization.translateText({ vi: 'Chưa có địa chỉ nào', en: 'No saved addresses', fr: 'Aucune adresse', es: 'Sin direcciones', id: 'Belum ada alamat', ja: '保存済み住所なし', zh: '暂无地址', ko: '저장된 주소 없음', hi: 'कोई पता नहीं', ar: 'لا توجد عناوين' })}
                            </div>
                        )}
                        {savedAddresses && savedAddresses.map(function (addr, idx) {
                            return (
                                <div key={addr.id || idx} className="v2-address-picker__addr-row" onClick={function () { selectSaved(addr); }}>
                                    <div className="v2-address-picker__addr-name">{addr.contact_name || addr.transport_contact_name}</div>
                                    <div className="v2-address-picker__addr-phone">{addr.contact_phone || addr.transport_contact_phone}</div>
                                    <div className="v2-address-picker__addr-detail">{addr.address || addr.transport_address}</div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* New tab */}
                {activeTab === 'new' && (
                    <div className="v2-address-picker__form">
                        {/* Máy chủ chưa khai khoá tìm địa chỉ → ẩn cả nhãn lẫn ô, tránh nhãn trơ trọi. */}
                        {searchEnabled ? (
                            <div className="v2-address-picker__field">
                                <label>{AntLocalization.translateText({ vi: 'Tìm nhanh địa chỉ', en: 'Quick address search', fr: 'Recherche d\'adresse', es: 'Buscar dirección', id: 'Cari alamat cepat', ja: '住所クイック検索', zh: '快速搜索地址', ko: '주소 빠른 검색', hi: 'पता जल्दी खोजें', ar: 'بحث سريع عن عنوان' })}</label>
                                <AntAddressSearch country={country} language={locale} onSelect={onSearchSelect} />
                            </div>
                        ) : null}
                        <div className="v2-address-picker__field-row">
                            <div className="v2-address-picker__field">
                                <label>{AntLocalization.translateText({ vi: 'Người nhận', en: 'Recipient', fr: 'Destinataire', es: 'Destinatario', id: 'Penerima', ja: '受取人', zh: '收件人', ko: '수령인', hi: 'प्राप्तकर्ता', ar: 'المستلم' })}</label>
                                <input type="text" value={newAddr.contact_name} onChange={function (e) { updateField('contact_name', e.target.value); }} autoFocus />
                            </div>
                            <div className="v2-address-picker__field">
                                <label>{AntLocalization.translateText({ vi: 'Số điện thoại', en: 'Phone', fr: 'Téléphone', es: 'Teléfono', id: 'Telepon', ja: '電話番号', zh: '电话', ko: '전화번호', hi: 'फ़ोन', ar: 'الهاتف' })}</label>
                                <input type="text" value={newAddr.contact_phone} onChange={function (e) { updateField('contact_phone', e.target.value); }} />
                            </div>
                        </div>
                        <div className="v2-address-picker__field">
                            <label>{AntLocalization.translateText({ vi: 'Địa chỉ', en: 'Address', fr: 'Adresse', es: 'Dirección', id: 'Alamat', ja: '住所', zh: '地址', ko: '주소', hi: 'पता', ar: 'العنوان' })}</label>
                            <input type="text" value={newAddr.address} onChange={function (e) { updateField('address', e.target.value); }} />
                        </div>

                        {mode === 'postal' ? (
                            /* Nước dùng mã bưu chính (vd SG) — không cascade hành chính */
                            <div className="v2-address-picker__field">
                                <label>{AntLocalization.translateText({ vi: 'Mã bưu chính', en: 'Postal Code', fr: 'Code postal', es: 'Código postal', id: 'Kode pos', ja: '郵便番号', zh: '邮政编码', ko: '우편번호', hi: 'पिन कोड', ar: 'الرمز البريدي' })}</label>
                                <input type="text" value={newAddr.postal_code} onChange={function (e) { updateField('postal_code', e.target.value); }} />
                            </div>
                        ) : (
                            <div className="v2-address-picker__field-row v2-address-picker__field-row--cascade">
                                <div className="v2-address-picker__field">
                                    <label>{lbl1}</label>
                                    <select value={newAddr.adm1_code} onChange={function (e) { onProvinceChange(e.target.value); }}>
                                        <option value="">—</option>
                                        {provinces.map(function (p) { return <option key={p.code} value={p.code}>{p.name}</option>; })}
                                    </select>
                                </div>
                                {levelCount >= 2 && (
                                    <div className="v2-address-picker__field">
                                        <label>{lbl2}</label>
                                        <select value={newAddr.adm2_code} onChange={function (e) { onDistrictChange(e.target.value); }}>
                                            <option value="">—</option>
                                            {districts.map(function (d) { return <option key={d.code} value={d.code}>{d.name}</option>; })}
                                        </select>
                                    </div>
                                )}
                                {levelCount >= 3 && (
                                    <div className="v2-address-picker__field">
                                        <label>{lbl3}</label>
                                        <select value={newAddr.adm3_code} onChange={function (e) { onWardChange(e.target.value); }}>
                                            <option value="">—</option>
                                            {wards.map(function (w) { return <option key={w.code} value={w.code}>{w.name}</option>; })}
                                        </select>
                                    </div>
                                )}
                            </div>
                        )}
                        <button
                            className="v2-address-picker__submit"
                            onClick={submitNew}
                            disabled={!newAddr.address && !newAddr.contact_name}
                        >
                            {AntLocalization.translateText({ vi: 'Xác nhận', en: 'Confirm', fr: 'Confirmer', es: 'Confirmar', id: 'Konfirmasi', ja: '確認', zh: '确认', ko: '확인', hi: 'पुष्टि करें', ar: 'تأكيد' })}
                        </button>
                    </div>
                )}
            </div>
        </AntPopup>
    );
});

export default AntAddressPicker;
